import React, { useMemo, useState } from "react";

const normalizePairs = (pairs) =>
  Array.isArray(pairs)
    ? pairs.map((pair, idx) => ({
      id: String(pair?.id || `pair${idx + 1}`),
      left: {
        text: String(pair?.left?.text || ""),
        imgPath: String(pair?.left?.imgPath || ""),
      },
      right: {
        text: String(pair?.right?.text || ""),
        imgPath: String(pair?.right?.imgPath || ""),
      },
    }))
    : [];

const rotateItems = (items) => {
  if (items.length < 2) return items;
  const splitAt = Math.ceil(items.length / 2);
  return [...items.slice(splitAt), ...items.slice(0, splitAt)];
};

const CardContent = ({ item }) => (
  <>
    {item.imgPath && <img src={item.imgPath} alt={item.text || ""} />}
    {item.text && <span>{item.text}</span>}
  </>
);

const MatchingPairsQuestion = ({
  question,
  instruction,
  tip,
  leftTitle,
  rightTitle,
  pairs,
  onSolved,
  showNextButton,
  onNext,
  showFinishButton,
  onFinish,
}) => {
  const normalizedPairs = useMemo(() => normalizePairs(pairs), [pairs]);
  const targets = useMemo(() => rotateItems(normalizedPairs), [normalizedPairs]);
  const [placements, setPlacements] = useState({});
  const [selectedPairId, setSelectedPairId] = useState(null);
  const [checked, setChecked] = useState(false);
  const [solved, setSolved] = useState(false);

  const placedSourceIds = Object.values(placements).filter(Boolean);
  const foundCount = normalizedPairs.filter((pair) => placements[pair.id] === pair.id).length;
  const allPlaced = normalizedPairs.length > 0 && placedSourceIds.length === normalizedPairs.length;

  function placePair(targetId, sourceId) {
    if (solved || !sourceId) return;

    setPlacements((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([, placedSourceId]) => placedSourceId !== sourceId)
      );
      next[targetId] = sourceId;
      return next;
    });
    setChecked(false);
    setSelectedPairId(null);
  }

  function pickPlacedPair(targetId, sourceId) {
    if (solved || !sourceId) return;

    setPlacements((prev) => {
      const next = { ...prev };
      delete next[targetId];
      return next;
    });
    setChecked(false);
    setSelectedPairId(sourceId);
  }

  function reset() {
    setPlacements({});
    setSelectedPairId(null);
    setChecked(false);
    setSolved(false);
  }

  function check() {
    if (!allPlaced) return;
    setChecked(true);
    const isSolved = normalizedPairs.every((pair) => placements[pair.id] === pair.id);
    setSolved(isSolved);
    if (isSolved) onSolved();
  }

  return (
    <>
      {question && <div className="test-question question-title" dangerouslySetInnerHTML={{ __html: question }} />}

      {instruction && (
        <div className="matching-pairs-note matching-pairs-instruction">
          <strong>Як грати</strong>
          <p>{instruction}</p>
        </div>
      )}

      <div className="matching-pairs-board">
        <div className="matching-pairs-zone">
          {leftTitle && <span className="matching-pairs-zone-label">{leftTitle}</span>}
          <div className="matching-pairs-items">
            {normalizedPairs.map((pair) => {
              const isPlaced = placedSourceIds.includes(pair.id);
              const isSelected = selectedPairId === pair.id;

              return (
                <button
                  type="button"
                  key={pair.id}
                  className={[
                    "matching-pairs-card",
                    isPlaced ? "placed" : "",
                    isSelected ? "selected" : "",
                  ].filter(Boolean).join(" ")}
                  draggable={!isPlaced && !solved}
                  disabled={isPlaced || solved}
                  onClick={() => setSelectedPairId(isSelected ? null : pair.id)}
                  onDragStart={(event) => event.dataTransfer.setData("text/plain", pair.id)}
                >
                  <CardContent item={pair.left} />
                </button>
              );
            })}
          </div>
        </div>

        <div className="matching-pairs-zone">
          {rightTitle && <span className="matching-pairs-zone-label">{rightTitle}</span>}
          <div className="matching-pairs-items">
            {targets.map((pair) => {
              const placedPair = normalizedPairs.find((item) => item.id === placements[pair.id]);
              const status = checked
                ? placements[pair.id] === pair.id
                  ? "correct"
                  : "incorrect"
                : "";

              return (
                <button
                  type="button"
                  key={pair.id}
                  className={[
                    "matching-pairs-target",
                    placedPair ? "filled" : "",
                    status,
                  ].filter(Boolean).join(" ")}
                  onClick={() => (
                    selectedPairId
                      ? placePair(pair.id, selectedPairId)
                      : pickPlacedPair(pair.id, placements[pair.id])
                  )}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    placePair(pair.id, event.dataTransfer.getData("text/plain"));
                  }}
                >
                  <div className="matching-pairs-target-image">
                    <CardContent item={pair.right} />
                  </div>
                  {placedPair && (
                    <div className="matching-pairs-placed-card">
                      <CardContent item={placedPair.left} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="matching-pairs-actions">
        <button type="button" className="btn matching-pairs-reset" onClick={reset}>
          Скинути
        </button>
        <div className="matching-pairs-progress">
          Знайдено: {foundCount} з {normalizedPairs.length}
          <span>
            <i style={{ width: `${normalizedPairs.length ? (foundCount / normalizedPairs.length) * 100 : 0}%` }} />
          </span>
        </div>
        <button
          type="button"
          className="btn checkAnswer"
          onClick={check}
          disabled={!allPlaced || solved}
          style={{ display: "inline-block" }}
        >
          Перевірити
        </button>
      </div>

      {checked && !solved && (
        <div className="matching-pairs-feedback incorrect">
          Є пари, які потрібно переставити.
        </div>
      )}

      {solved && (
        <div className="matching-pairs-feedback correct">
          Усі пари складено правильно!
        </div>
      )}

      {tip && (
        <div className="matching-pairs-note matching-pairs-tip">
          <strong>Порада</strong>
          <p>{tip}</p>
        </div>
      )}

      {solved && showNextButton && (
        <button className="btn nextAnswer" onClick={onNext} style={{ display: "inline-block" }}>
          Наступне завдання
        </button>
      )}

      {solved && showFinishButton && (
        <button className="btn nextAnswer" onClick={onFinish} style={{ display: "inline-block" }}>
          Завершити
        </button>
      )}
    </>
  );
};

export default MatchingPairsQuestion;
